import React, { useState, useEffect, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

// Hotel Config → Lock Agents
// Lists the Lock Bridge agents registered for this property, lets admins
// register new ones, revoke, or rotate their tokens. The bootstrap token
// returned from /lock/agents/register is shown ONCE and must be copied
// into the agent's appsettings.json (or the first-run wizard input).
export default function PmsLockAgents({ token, selectedProperty }) {
  const [vendors, setVendors] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [registerOpen, setRegisterOpen] = useState(false);
  const [tokenModal, setTokenModal] = useState(null); // { token, prefix, agent_id, installer_url }

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [vRes, aRes] = await Promise.all([
        fetch('/api/v1/lock/vendors', { headers: headers() }),
        fetch('/api/v1/lock/agents', { headers: headers() })
      ]);
      if (!vRes.ok) throw new Error('Failed to load vendors');
      if (!aRes.ok) throw new Error('Failed to load agents');
      const v = await vRes.json();
      const a = await aRes.json();
      setVendors(v.vendors || []);
      setAgents(a.agents || []);
    } catch (e) {
      setError(e.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { loadAll(); }, [loadAll, selectedProperty?.id]);

  const handleRevoke = async (agentId, displayName) => {
    if (!window.confirm(`Revoke agent "${displayName}"? It will be kicked off and cannot reconnect.`)) return;
    try {
      const res = await fetch(`/api/v1/lock/agents/${agentId}/revoke`, { method: 'POST', headers: headers() });
      if (!res.ok) throw new Error((await res.json()).error || 'Revoke failed');
      await loadAll();
    } catch (e) { alert(e.message); }
  };

  const handleRotate = async (agentId, displayName) => {
    if (!window.confirm(`Rotate token for "${displayName}"? The old token stops working immediately and the agent must be reconfigured with the new one.`)) return;
    try {
      const res = await fetch(`/api/v1/lock/agents/${agentId}/rotate-token`, { method: 'POST', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Rotate failed');
      setTokenModal({ token: data.bootstrap_token, prefix: data.agent_token_prefix, agent_id: agentId, installer_url: '/agent-releases/lock-bridge/EasePmsLockBridgeSetup.exe' });
      await loadAll();
    } catch (e) { alert(e.message); }
  };

  const statusPill = (status) => {
    const colors = {
      ONLINE:  { bg: '#dcfce7', fg: '#166534', border: '#86efac', dot: '#16a34a' },
      OFFLINE: { bg: '#f3f4f6', fg: '#4b5563', border: '#d1d5db', dot: '#9ca3af' },
      PENDING: { bg: '#fef3c7', fg: '#92400e', border: '#fcd34d', dot: '#f59e0b' },
      REVOKED: { bg: '#fee2e2', fg: '#991b1b', border: '#fca5a5', dot: '#dc2626' }
    };
    const c = colors[status] || colors.PENDING;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
        borderRadius: 999, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 600
      }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot }} />
        {status}
      </span>
    );
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const now = Date.now();
    const ago = now - d.getTime();
    if (ago < 60_000)      return `${Math.floor(ago / 1000)}s ago`;
    if (ago < 3_600_000)   return `${Math.floor(ago / 60_000)}m ago`;
    if (ago < 86_400_000)  return `${Math.floor(ago / 3_600_000)}h ago`;
    return d.toLocaleString();
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e293b' }}>Lock Agents</h2>
          <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: 4 }}>
            On-prem Lock Bridge agents installed at this property
          </div>
        </div>
        <button
          onClick={() => setRegisterOpen(true)}
          style={{
            background: '#2563eb', color: '#fff', border: 'none',
            padding: '10px 18px', borderRadius: 6, fontWeight: 600, cursor: 'pointer'
          }}
        >
          + Register Agent
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading…</div>
      ) : agents.length === 0 ? (
        <div style={{
          background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8,
          padding: 40, textAlign: 'center', color: '#475569'
        }}>
          <div style={{ fontSize: '1.05rem', marginBottom: 8 }}>No Lock Bridge agents yet.</div>
          <div style={{ fontSize: '0.9rem' }}>Register an agent to integrate this property's on-prem lock system with EASEPMS.</div>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th style={th}>Display name</th>
                <th style={th}>Vendor</th>
                <th style={th}>Transport</th>
                <th style={th}>Encoders</th>
                <th style={th}>Status</th>
                <th style={th}>Last seen</th>
                <th style={th}>Version</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.agent_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={td}><b>{a.display_name}</b><div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>ID {a.agent_id}</div></td>
                  <td style={td}>{vendorDisplay(vendors, a.vendor)}</td>
                  <td style={td}>{a.transport_kind || '—'}<div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{a.transport_target || ''}</div></td>
                  <td style={td}>
                    {Array.isArray(a.encoders_json) && a.encoders_json.length > 0
                      ? a.encoders_json.map((e, i) => (
                          <span key={i} style={{
                            display: 'inline-block', margin: '1px 4px 1px 0',
                            background: '#eff6ff', color: '#1e40af',
                            border: '1px solid #bfdbfe', borderRadius: 4,
                            padding: '1px 6px', fontSize: '0.7rem'
                          }}>#{e.id || e.Id} {e.kind || e.Kind}</span>
                        ))
                      : '—'}
                  </td>
                  <td style={td}>{statusPill(a.status)}</td>
                  <td style={td}>{fmtDate(a.last_seen_at)}</td>
                  <td style={td}>{a.agent_version || '—'}</td>
                  <td style={td}>
                    {a.status !== 'REVOKED' && (
                      <>
                        <button onClick={() => handleRotate(a.agent_id, a.display_name)} style={actionBtn}>Rotate token</button>
                        <button onClick={() => handleRevoke(a.agent_id, a.display_name)} style={{ ...actionBtn, color: '#991b1b' }}>Revoke</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {registerOpen && (
        <RegisterAgentModal
          vendors={vendors}
          onClose={() => setRegisterOpen(false)}
          onRegistered={(result) => {
            setRegisterOpen(false);
            setTokenModal(result);
            loadAll();
          }}
          headers={headers}
        />
      )}

      {tokenModal && <TokenModal data={tokenModal} onClose={() => setTokenModal(null)} />}
    </div>
  );
}

function vendorDisplay(vendors, key) {
  const v = vendors.find((x) => x.vendor_key === key);
  return v ? v.display_name : key;
}

function RegisterAgentModal({ vendors, onClose, onRegistered, headers }) {
  const { isMobile } = useIsMobile();
  const [displayName, setDisplayName] = useState('Reception PC 1');
  const [vendor, setVendor] = useState(vendors[0]?.vendor_key || '');
  const [transport, setTransport] = useState('tcp');
  const [host, setHost] = useState('127.0.0.1');
  const [tcpPort, setTcpPort] = useState(6669);
  const [port, setPort] = useState('COM3');
  const [baudRate, setBaudRate] = useState(9600);
  const [encoders, setEncoders] = useState([{ id: 2, kind: 'motorized' }]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  // Schema-driven config for non-Onity vendors (values keyed by form_schema field key).
  const [schemaCfg, setSchemaCfg] = useState({});

  const manifest = vendors.find((v) => v.vendor_key === vendor);
  // Onity keeps its bespoke encoder form; every other vendor is rendered from its
  // form_schema (transport enum + conditional fields), so new vendors need no UI code.
  const isOnity = vendor === 'ONITY_V71';
  const fields = manifest?.form_schema?.fields || [];
  const isVisible = (f) => !f.show_when
    || Object.entries(f.show_when).every(([k, v]) => String(schemaCfg[k] ?? '') === String(v));

  // Seed schema defaults whenever a non-Onity vendor is selected.
  useEffect(() => {
    if (isOnity) return;
    const init = {};
    for (const f of (manifest?.form_schema?.fields || [])) {
      if (f.default !== undefined) init[f.key] = f.default;
    }
    setSchemaCfg(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor]);

  const addEncoder = () => setEncoders([...encoders, { id: (encoders[encoders.length - 1]?.id || 1) + 1, kind: 'motorized' }]);
  const removeEncoder = (i) => setEncoders(encoders.filter((_, idx) => idx !== i));
  const updateEncoder = (i, field, value) => {
    const next = [...encoders];
    next[i] = { ...next[i], [field]: field === 'id' ? Number(value) : value };
    setEncoders(next);
  };

  const submit = async () => {
    if (!displayName.trim()) return setErr('Display name is required');
    if (!vendor) return setErr('Vendor is required');

    let vendorConfig, transportKind, transportTarget;
    if (isOnity) {
      if (encoders.length === 0) return setErr('At least one encoder must be configured');
      const reserved = manifest?.reserved_encoder_ids || [];
      for (const e of encoders) {
        if (reserved.includes(Number(e.id))) {
          return setErr(`Encoder ${e.id} is reserved for the vendor management workstation`);
        }
      }
      vendorConfig = transport === 'serial'
        ? { Transport: 'serial', Port: port, BaudRate: Number(baudRate), Encoders: encoders.map(e => ({ Id: Number(e.id), Kind: e.kind })) }
        : { Transport: 'tcp',    Host: host, TcpPort: Number(tcpPort), Encoders: encoders.map(e => ({ Id: Number(e.id), Kind: e.kind })) };
      transportKind = transport;
      transportTarget = transport === 'serial' ? port : `${host}:${tcpPort}`;
    } else {
      // Build vendor_config from the visible schema fields, coercing by type.
      vendorConfig = {};
      for (const f of fields) {
        if (!isVisible(f)) continue;
        let val = schemaCfg[f.key];
        if (val === undefined || val === '') val = f.default;
        if (f.type === 'integer') val = (val === undefined || val === null || val === '') ? null : Number(val);
        else if (f.type === 'boolean') val = Boolean(val);
        vendorConfig[f.key] = val ?? '';
      }
      if (!vendorConfig.transport) return setErr('Transport is required');
      transportKind = String(vendorConfig.transport);
      transportTarget = vendorConfig.rest_base_url || vendorConfig.com_port || '';
    }

    setSubmitting(true); setErr('');
    try {
      const res = await fetch('/api/v1/lock/agents/register', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          display_name: displayName.trim(),
          vendor,
          transport_kind: transportKind,
          transport_target: transportTarget,
          vendor_config: vendorConfig,
          supports_door_events: Boolean(manifest?.supports_door_events)
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Register failed');
      onRegistered({
        token: data.bootstrap_token,
        prefix: data.agent_token_prefix,
        agent_id: data.agent_id,
        installer_url: data.installer_url
      });
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="Register Lock Bridge Agent" onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '180px 1fr', gap: isMobile ? 6 : 10, alignItems: 'center' }}>
        <label style={lbl}>Display name</label>
        <input style={inp} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />

        <label style={lbl}>Vendor</label>
        <select style={inp} value={vendor} onChange={(e) => setVendor(e.target.value)}>
          {vendors.map((v) => <option key={v.vendor_key} value={v.vendor_key}>{v.display_name}</option>)}
        </select>

        {isOnity ? (
          <>
            <label style={lbl}>Transport</label>
            <div>
              <label style={{ marginRight: 12 }}>
                <input type="radio" checked={transport === 'tcp'} onChange={() => setTransport('tcp')} /> TCP socket
              </label>
              <label>
                <input type="radio" checked={transport === 'serial'} onChange={() => setTransport('serial')} /> Serial COM port
              </label>
            </div>

            {transport === 'tcp' ? (
              <>
                <label style={lbl}>Host</label>
                <input style={inp} value={host} onChange={(e) => setHost(e.target.value)} placeholder="127.0.0.1" />
                <label style={lbl}>TCP port</label>
                <input style={inp} type="number" value={tcpPort} onChange={(e) => setTcpPort(e.target.value)} />
              </>
            ) : (
              <>
                <label style={lbl}>COM port</label>
                <input style={inp} value={port} onChange={(e) => setPort(e.target.value)} placeholder="COM3" />
                <label style={lbl}>Baud rate</label>
                <select style={inp} value={baudRate} onChange={(e) => setBaudRate(e.target.value)}>
                  {[1200, 2400, 4800, 9600, 19200].map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </>
            )}

            <label style={{ ...lbl, alignSelf: 'start', paddingTop: 8 }}>Encoders</label>
            <div>
              {encoders.map((e, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                  <input type="number" style={{ ...inp, width: 80 }} value={e.id} onChange={(ev) => updateEncoder(i, 'id', ev.target.value)} />
                  <select style={{ ...inp, width: 140 }} value={e.kind} onChange={(ev) => updateEncoder(i, 'kind', ev.target.value)}>
                    <option value="motorized">Motorized</option>
                    <option value="manual">Manual / swipe</option>
                  </select>
                  <button onClick={() => removeEncoder(i)} style={{ ...actionBtn, color: '#991b1b' }}>Remove</button>
                </div>
              ))}
              <button onClick={addEncoder} style={actionBtn}>+ Add encoder</button>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>
                Reserved encoder IDs: {(manifest?.reserved_encoder_ids || []).join(', ') || '—'}
              </div>
            </div>
          </>
        ) : (
          fields.map((f) => {
            if (!isVisible(f)) return null;
            const val = schemaCfg[f.key] !== undefined ? schemaCfg[f.key] : (f.default !== undefined ? f.default : '');
            const set = (v) => setSchemaCfg((s) => ({ ...s, [f.key]: v }));
            let control;
            if (f.type === 'enum') {
              control = (
                <select style={inp} value={val} onChange={(e) => set(e.target.value)}>
                  {(f.options || []).map((o) => {
                    const ov = (o && typeof o === 'object') ? o.value : o;
                    const ol = (o && typeof o === 'object') ? o.label : String(o);
                    return <option key={String(ov)} value={ov}>{ol}</option>;
                  })}
                </select>
              );
            } else if (f.type === 'boolean') {
              control = (
                <label style={{ fontSize: '0.85rem' }}>
                  <input type="checkbox" checked={Boolean(val)} onChange={(e) => set(e.target.checked)} /> Enabled
                </label>
              );
            } else if (f.type === 'integer') {
              control = <input style={inp} type="number" value={val} onChange={(e) => set(e.target.value)} />;
            } else if (f.type === 'password') {
              control = <input style={inp} type="password" value={val} onChange={(e) => set(e.target.value)} />;
            } else {
              control = <input style={inp} value={val} onChange={(e) => set(e.target.value)} />;
            }
            return (
              <React.Fragment key={f.key}>
                <label style={lbl}>{f.label}{f.required ? ' *' : ''}</label>
                {control}
              </React.Fragment>
            );
          })
        )}
      </div>

      {err && <div style={{ marginTop: 12, background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 6 }}>{err}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
        <button onClick={onClose} style={btnSecondary}>Cancel</button>
        <button onClick={submit} disabled={submitting} style={btnPrimary}>
          {submitting ? 'Registering…' : 'Register & generate token'}
        </button>
      </div>
    </ModalShell>
  );
}

function TokenModal({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(data.token); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };
  return (
    <ModalShell title="Bootstrap token — copy now (shown only once)" onClose={onClose}>
      <div style={{
        background: '#fef3c7', color: '#92400e', padding: 12, borderRadius: 6,
        marginBottom: 14, border: '1px solid #fcd34d', fontSize: '0.875rem'
      }}>
        <b>This token is shown only once.</b> Save it now — once you close this dialog you'll need to rotate to get a new one.
      </div>
      <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: 8 }}>Token prefix: <code>{data.prefix}</code></div>
      <textarea
        readOnly
        value={data.token}
        style={{
          width: '100%', minHeight: 90, fontFamily: 'monospace', fontSize: '0.8rem',
          padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', resize: 'vertical'
        }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={copy} style={btnPrimary}>{copied ? 'Copied ✓' : 'Copy token'}</button>
        {data.installer_url && (
          <a href={data.installer_url} style={{ ...btnSecondary, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
            Download installer
          </a>
        )}
        <button onClick={onClose} style={btnSecondary}>Close</button>
      </div>
      <div style={{ marginTop: 16, fontSize: '0.8rem', color: '#475569', lineHeight: 1.5 }}>
        <div style={{ marginBottom: 4 }}><b>Next steps:</b></div>
        <ol style={{ marginTop: 4, paddingLeft: 20 }}>
          <li>Install <code>EasePmsLockBridgeSetup.exe</code> on the Reception PC.</li>
          <li>When the wizard prompts for an agent token, paste this value.</li>
          <li>The agent registers itself, comes online, and this screen shows <b>ONLINE</b>.</li>
        </ol>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(15, 23, 42, 0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }}>
      <div style={{
        background: '#fff', borderRadius: 10, padding: 22,
        maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 24px 60px rgba(15,23,42,0.25)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.1rem' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#64748b', cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ----- inline style helpers -----
const th = { padding: '10px 12px', textAlign: 'left', fontSize: '0.78rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 };
const td = { padding: '10px 12px', verticalAlign: 'top', color: '#0f172a' };
const lbl = { color: '#475569', fontSize: '0.875rem', fontWeight: 500 };
const inp = { padding: '7px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' };
const actionBtn = { background: 'transparent', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '0.85rem', marginRight: 8, padding: 0 };
const btnPrimary   = { background: '#2563eb', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { background: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' };
